import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ClinqLogo } from "../components/ClinqLogo";
import { CheckCircle2, GraduationCap, ExternalLink } from "lucide-react";

const OPTIONS = [
  { key: "pitch_deck", label: "Pitch Deck Masterclass", blurb: "Build an investor-grade deck that tells your story and your numbers." },
  { key: "financial_model", label: "Financial Model Masterclass", blurb: "Three-statement model, assumptions funders test, and the ask." },
  { key: "management_accounts", label: "Management Accounts Masterclass", blurb: "Monthly reporting that lenders and investors trust." },
  { key: "business_valuation", label: "Business Valuation Masterclass", blurb: "What your business is worth, how funders will value it, and how to defend it." },
  { key: "isbi_cohort", label: "Funder Readiness Day Training — ISBI Cohort", blurb: "Full-day cohort programme at Strathmore Business School, run with ISBI.", tag: "Strathmore Business School" },
];

export default function MasterclassInterest() {
  const [form, setForm] = useState({ full_name: "", email: "", company_name: "" });
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const submit = async (e) => {
    e.preventDefault();
    if (picked.length === 0) { setErr("Select at least one masterclass."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/public/masterclass-interest", { ...form, interests: picked });
      setDone(true);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Something went wrong — please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F7F7] text-neutral-900" data-testid="masterclass-interest-page">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5">
        <Link to="/" data-testid="mci-logo-link"><ClinqLogo size="md" tone="teal" /></Link>
        <Link to="/login" className="text-sm font-bold text-[#0A8B85] hover:underline" data-testid="mci-login-link">Log in to Clinq</Link>
      </header>

      <section className="bg-[#0B2A28] px-5 py-14 text-white sm:py-16">
        <div className="mx-auto max-w-5xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#0A8B85]/60 bg-[#0A8B85]/20 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#7BD3CE]">
            <GraduationCap size={13} /> Masterclasses &amp; ISBI Cohorts
          </span>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl" data-testid="mci-headline">
            Learn what it takes to close capital
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B9D9D6] md:text-lg">
            Our Masterclasses are run in conjunction with{" "}
            <a href="https://isbi.or.ke" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-white underline decoration-[#0A8B85] underline-offset-4" data-testid="mci-isbi-link">
              ISBI Strathmore Business School <ExternalLink size={14} />
            </a>{" "}
            and designed for founders. Tell us which sessions interest you — no sign-in needed — and we'll reach out with the next dates.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-2xl px-5 py-12 pb-20">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8" data-testid="mci-form-card">
          {done ? (
            <div className="text-center" data-testid="mci-success">
              <CheckCircle2 className="mx-auto h-10 w-10 text-[#0A8B85]" />
              <p className="mt-3 text-lg font-bold">You're on the list</p>
              <p className="mt-1 text-sm text-neutral-600">
                Thank you, {form.full_name.split(" ")[0]}. The Clinq Capital Team will email you at {form.email} with the next available dates for the sessions you chose.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#0A8B85]">Step 1</p>
                <h2 className="text-base font-bold md:text-lg">Which sessions interest you?</h2>
                <div className="mt-3 space-y-2">
                  {OPTIONS.map((o) => {
                    const on = picked.includes(o.key);
                    return (
                      <label key={o.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition-colors ${on ? "border-[#0A8B85] bg-[#0A8B85]/5" : "border-neutral-200 hover:border-neutral-300"}`}
                        data-testid={`mci-option-${o.key}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(o.key)} className="mt-1 h-4 w-4 accent-[#0A8B85]" />
                        <span>
                          <span className="block text-sm font-bold text-neutral-900">{o.label}
                            {o.tag && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">{o.tag}</span>}
                          </span>
                          <span className="block text-xs text-neutral-600">{o.blurb}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#0A8B85]">Step 2</p>
                <h2 className="text-base font-bold md:text-lg">Your details</h2>
                <div className="mt-3 space-y-3">
                  {[["full_name", "Full name", "text", "Amina Wanjiru"], ["email", "Email address", "email", "you@business.com"], ["company_name", "Business name", "text", "Wanjiru Foods Ltd"]].map(([k, label, type, ph]) => (
                    <label key={k} className="block">
                      <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}</span>
                      <input type={type} required value={form[k]} onChange={set(k)} placeholder={ph}
                        className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85] focus:ring-2 focus:ring-[#0A8B85]/20"
                        data-testid={`mci-input-${k}`} />
                    </label>
                  ))}
                </div>
              </div>
              {err && <p className="text-sm text-red-600" data-testid="mci-error">{err}</p>}
              <button type="submit" disabled={busy}
                className="w-full rounded-full bg-[#0A8B85] px-6 py-3 text-sm font-bold text-white hover:bg-[#0C9F97] disabled:opacity-50"
                data-testid="mci-submit">
                {busy ? "Sending…" : "Express my interest"}
              </button>
              <p className="text-center text-[11px] text-neutral-500">We'll only use these details to contact you about the sessions you selected.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
